<template>
  <div>
    <div v-if="!inline" class="visitor-address-summary">
      <p class="address-label">Visitor address</p>
      <p v-if="loading" role="status">Loading addresses...</p>
      <p v-else-if="error" role="alert">{{ error }} <v-btn variant="text" size="small" @click="refresh">Retry</v-btn></p>
      <ul v-else-if="primaryHostname" class="visible-addresses">
        <li>
          <a :href="`https://${primaryHostname}`" target="_blank" rel="noopener noreferrer"><v-icon size="18" aria-hidden="true">mdi-open-in-new</v-icon>https://{{ primaryHostname }}</a>
          <SiteQrCode :site="site" :hostname="primaryHostname" compact />
        </li>
      </ul>
      <p v-else>No visitor address assigned yet.</p>
      <v-btn variant="text" size="small" prepend-icon="mdi-pencil-outline" @click="open">Manage addresses</v-btn>
    </div>
    <component :is="inline ? 'div' : VDialog" v-model="opened" :max-width="inline ? undefined : 620" :persistent="busy">
      <v-card :variant="inline ? 'flat' : undefined">
        <v-card-title v-if="!inline">Visitor addresses: {{ site.name }}</v-card-title>
        <v-card-text>
          <p>This site has one visitor address. DNS and an IIS HTTPS binding must also be configured.</p>
          <p v-if="loading">Loading addresses...</p>
          <template v-else>
            <div v-if="primaryHostname" class="hostname-row hostname-row--current">
              <a :href="`https://${primaryHostname}`" target="_blank" rel="noopener noreferrer">{{ primaryHostname }}</a>
              <div class="hostname-actions">
                <SiteQrCode :site="site" :hostname="primaryHostname" compact />
                <v-btn variant="outlined" size="small" prepend-icon="mdi-pencil-outline" :disabled="busy" @click="startChange">Change</v-btn>
                <v-btn variant="text" color="error" size="small" :disabled="busy" @click="remove(primaryHostname)">Remove</v-btn>
              </div>
            </div>
            <ul v-if="extraHostnames.length" class="legacy-addresses">
              <li v-for="hostname in extraHostnames" :key="hostname" class="hostname-row">
                <a :href="`https://${hostname}`" target="_blank" rel="noopener noreferrer">{{ hostname }}</a>
                <div class="hostname-actions">
                  <SiteQrCode :site="site" :hostname="hostname" compact />
                  <v-btn variant="text" color="error" size="small" :disabled="busy" @click="remove(hostname)">Remove extra</v-btn>
                </div>
              </li>
            </ul>
            <p v-if="!primaryHostname">No visitor address assigned yet.</p>
          </template>
          <v-form v-if="!loading && (!primaryHostname || editing)" ref="form" class="hostname-form" @submit.prevent="add">
            <v-text-field v-model="subdomain" label="Visitor subdomain" placeholder="quin" :suffix="visitorDomainSuffix"
              hint="Enter only the subdomain name." persistent-hint :disabled="busy || loading"
              :rules="subdomainRules" @blur="cleanSubdomainInput" />
            <div class="hostname-form-actions">
              <v-btn v-if="editing" variant="text" :disabled="busy" @click="cancelChange">Cancel</v-btn>
              <v-btn class="save-action" type="submit" color="primary" :loading="busy" :disabled="loading || !subdomain.trim()">{{ primaryHostname ? 'Save new address' : 'Set address' }}</v-btn>
            </div>
          </v-form>
          <p v-if="message" role="status" class="mt-3">{{ message }}</p>
          <p v-if="error" role="alert" class="text-error mt-3">{{ error }}</p>
        </v-card-text>
        <v-card-actions v-if="!inline"><v-spacer /><v-btn :disabled="busy" @click="opened = false">Close</v-btn></v-card-actions>
      </v-card>
    </component>
    <SiteQrCode ref="createdQr" :site="site" :show-thumbnail="false" />
  </div>
</template>
<script setup>
import { showConfirm } from '@/utils/messages';

import { computed, ref, onMounted } from 'vue';
import { VDialog } from 'vuetify/components';
import api from '@/api/client';
import SiteQrCode from '@/components/SiteQrCode.vue';
import {
  buildVisitorHostname,
  isReservedVisitorSubdomain,
  isValidVisitorSubdomain,
  normalizeVisitorSubdomain,
  visitorDomainSuffix,
} from '@/utils/visitorHostnames';
const props = defineProps({ site: { type: Object, required: true }, inline: Boolean });
const opened = ref(false), busy = ref(false), loading = ref(false);
const form = ref(null), subdomain = ref(''), hostnames = ref([]), error = ref(''), message = ref('');
const editing = ref(false);
const createdQr = ref(null);
const subdomainRules = computed(() => [required, subdomainFormat, subdomainReserved, subdomainUnused]);
const primaryHostname = computed(() => hostnames.value[0] || '');
const extraHostnames = computed(() => hostnames.value.slice(1));
onMounted(() => { if (props.inline) open(); else refresh(); });
const endpoint = () => `/sites/${props.site.id}/hostnames`;
const describeError = err => err.response?.data?.message || 'Unable to update visitor addresses. Check your access and try again.';
const required = value => Boolean(normalizeVisitorSubdomain(value)) || 'Required';
const subdomainFormat = value => isValidVisitorSubdomain(value) || 'Use one subdomain name, for example "quin".';
const subdomainReserved = value => !isReservedVisitorSubdomain(value) || 'That subdomain is reserved.';
const subdomainUnused = value => {
  const normalized = normalizeVisitorSubdomain(value);
  if (!normalized) return true;
  const hostname = buildVisitorHostname(normalized);
  return !hostnames.value.some(host => host.toLowerCase() === hostname) || 'That visitor address is already assigned to this site.';
};
function cleanSubdomainInput() {
  subdomain.value = normalizeVisitorSubdomain(subdomain.value);
}
async function load() { hostnames.value = (await api.get(endpoint())).data; }
async function open() {
  opened.value = true; await refresh();
}
async function refresh() {
  loading.value = true; error.value = ''; message.value = ''; hostnames.value = []; editing.value = false;
  try { await load(); } catch (err) { error.value = describeError(err); }
  finally { loading.value = false; }
}
function startChange() {
  editing.value = true;
  subdomain.value = '';
  message.value = '';
  error.value = '';
  form.value?.resetValidation?.();
}
function cancelChange() {
  editing.value = false;
  subdomain.value = '';
  form.value?.resetValidation?.();
}
async function add() {
  if (busy.value) return;
  cleanSubdomainInput();
  if (!(await form.value.validate()).valid) return;
  busy.value = true; error.value = ''; message.value = '';
  try {
    const hostname = buildVisitorHostname(subdomain.value);
    if (hostnames.value.some(host => host.toLowerCase() === hostname)) {
      error.value = 'That visitor address is already assigned to this site.';
      return;
    }
    const existing = primaryHostname.value;
    if (existing && !(await showConfirm(`Change visitor address from ${existing} to ${hostname}? Existing printed QR codes and links may stop working.`))) return;
    await api.post(endpoint(), { hostname });
    subdomain.value = ''; form.value.resetValidation(); editing.value = false; await load(); message.value = existing ? 'Address changed. The new address will work after DNS and IIS are configured.' : 'Address saved. The address will work after DNS and IIS are configured.';
    await createdQr.value.open(hostname);
  } catch (err) { error.value = describeError(err); }
  finally { busy.value = false; }
}
async function remove(host) {
  if (busy.value || !(await showConfirm(`Remove ${host}? New online visits to this address will no longer open this site.`))) return;
  busy.value = true; error.value = ''; message.value = '';
  try { await api.delete(`${endpoint()}/${encodeURIComponent(host)}`); await load(); editing.value = false; message.value = 'Address removed.'; }
  catch (err) { error.value = describeError(err); }
  finally { busy.value = false; }
}
</script>
<style scoped>
.visitor-address-summary { margin: 16px 0; }
.address-label { font-size: 12px; color: #596575; margin-bottom: 6px; }
.visible-addresses { list-style: none; margin: 0 0 8px; }
.visible-addresses li { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; }
.visible-addresses li + li { margin-top: 6px; }
.visible-addresses a { display: inline-flex; align-items: baseline; gap: 8px; color: #1e6d6f; font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
.visible-addresses .v-icon { flex-shrink: 0; }

.hostname-row { display: flex; gap: 12px; align-items: center; justify-content: space-between; flex-wrap: wrap; }
.hostname-row--current { padding: 10px 0; border-bottom: 1px solid #dfe8e4; }
.hostname-row a { overflow-wrap: anywhere; } ul { padding: 0; margin: 16px 0; }
.hostname-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; }
.legacy-addresses { color: #596575; }
.hostname-form { margin-top: 14px; }
.hostname-form-actions { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px; }
</style>
